import { LogOut, MessageSquare, Home, User, ChevronDown } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import type { AppUser } from "../../types/auth";
import { LanguageSwitcher } from "../common/language-switcher";

interface UserSettings {
  name: string;
  avatar: string;
  language: string;
}

interface TopNavProps {
  user: AppUser | null;
  onSignOut?: () => void;
}

export function TopNav({ user, onSignOut }: TopNavProps) {
  const { t } = useTranslation();
  const location = useLocation();
  const isChat = location.pathname === "/chat";
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [userSettings, setUserSettings] = useState<UserSettings | null>(null);

  useEffect(() => {
    const saved = localStorage.getItem("userSettings");
    if (saved) {
      setUserSettings(JSON.parse(saved));
    }
  }, []);
  
  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-white border-b border-gray-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-14">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 bg-gray-900 rounded-lg flex items-center justify-center">
              <MessageSquare className="w-4 h-4 text-white" />
            </div>
            <span className="text-lg font-semibold text-gray-900">Persona AI</span>
          </Link>

          {/* Right */}
          <div className="flex items-center gap-3">
            {isChat && (
              <Link
                to="/"
                className="text-sm text-gray-500 hover:text-gray-700 transition"
              >
                {t('home.recentChats')}
              </Link>
            )}
            
            {/* 语言切换器 */}
            <LanguageSwitcher compact />
            
            {/* 用户头像下拉菜单 */}
            <div className="relative">
              <button
                onClick={() => setShowUserMenu(!showUserMenu)}
                className="flex items-center gap-2 px-3 py-1.5 rounded-lg hover:bg-gray-100 transition"
              >
                <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center overflow-hidden">
                  {userSettings?.avatar ? (
                    <img 
                      src={userSettings.avatar} 
                      alt="头像" 
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <User className="w-4 h-4 text-gray-600" />
                  )}
                </div>
                <span className="text-sm text-gray-700">
                  {userSettings?.name || user?.nickname || "用户"}
                </span>
                <ChevronDown className="w-3 h-3 text-gray-400" />
              </button>
              {showUserMenu && (
                <div className="absolute right-0 top-full mt-1 w-40 bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden z-50">
                  <Link
                    to="/settings"
                    onClick={() => setShowUserMenu(false)}
                    className="w-full text-left px-4 py-2 text-sm hover:bg-gray-50 block"
                  >
                    {t('nav.settings')}
                  </Link>
                  <Link
                    to="/personas"
                    onClick={() => setShowUserMenu(false)}
                    className="w-full text-left px-4 py-2 text-sm hover:bg-gray-50 block"
                  >
                    {t('nav.personas')}
                  </Link>
                  <div className="border-t border-gray-100"></div>
                  {onSignOut && (
                    <button
                      onClick={() => {
                        setShowUserMenu(false);
                        onSignOut();
                      }}
                      className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-gray-50 flex items-center gap-2"
                    >
                      <LogOut className="w-4 h-4" />
                      {t('nav.signOut')}
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </header>
  );
}
